import { useState, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import '../../fonts/fonts.css'
import paymentIcon  from '../../images/icon-payment.png'
import downloadIcon from '../../images/icon-download.png'
import searchIcon   from '../../images/search.svg'
import {
  PageWrapper,
  PageHeader, PageIconBox, PageIconImg, PageTitle, PageSubtitle,
  FilterArea, FilterRow, FilterField, FilterFieldLabel, FilterSearchBox,
  FilterActionBtn, FilterResetBtn,
  SearchArea, SearchWrapper, SearchImg, SearchInput, SearchButton,
  TableArea, TableCard, EmptyRow,
  TableHeader, HeaderCell, TableBody, TableRow, Cell,
  AgencyBlock, AgencyRow, ArrowBtn, AgencyNameText, AgencyStats, StatPill,
  SubTableWrap, SubTableHead, SubHeadCell, SubRow, SubCell,
  StatusBadge, ActionBtn,
} from './styledComponents'

const apiBase = process.env.REACT_APP_TGDAPI_SERVER || ''

const STATUS_LABELS = { completed: 'Payé', pending: 'En attente', failed: 'Échoué', cancelled: 'Annulé', refunded: 'Remboursé' }

// Les commandes pending/cancelled sont des paiements Stripe abandonnés : on ne les montre pas
const VISIBLE_STATUSES = ['completed', 'failed', 'refunded']
const parseOrders = (data) =>
  (Array.isArray(data) ? data : (data.orders || [])).filter(order => VISIBLE_STATUSES.includes(order.status))

const fmtPrice = (centimes) =>
  (centimes / 100).toFixed(2).replace('.', ',') + ' €'

const fmtDate = (isoStr) => {
  if (!isoStr) return '—'
  return new Date(isoStr).toLocaleDateString('fr-FR')
}

const resolveAgencyId = (ag) =>
  ag.agencyId || ag._id || (ag.id != null ? String(ag.id) : null) || ag.agencyName || ag.agencyname

const groupByOrderNumber = (orders) => {
  const map = {}
  orders.forEach(order => {
    const key = order.orderNumber || order._id
    if (!map[key]) {
      map[key] = {
        key,
        orderNumber: key,
        date: order.createdAt,
        status: order.status,
        items: [],
        totalAmount: 0,
      }
    }
    ;(order.items || []).forEach(item => {
      map[key].items.push({ name: item.name, quantity: item.quantity, unitAmount: item.unitAmount })
      map[key].totalAmount += (item.unitAmount || 0) * (item.quantity || 1)
    })
    if (new Date(order.createdAt) > new Date(map[key].date)) {
      map[key].date = order.createdAt
    }
  })
  return Object.values(map).sort((a, b) => new Date(b.date) - new Date(a.date))
}


const deduplicateAgencies = (agencies) => {
  const seen = new Set()
  return (agencies || []).filter(ag => {
    const key = resolveAgencyId(ag) || `name:${ag.agencyName || ag.agencyname}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const MyPurchases = () => {
  const agencyToken = useSelector(state => state.toJS().global.agencytoken) || ''
  const isAdmin     = useSelector(state => state.toJS().global.isAdmin)
  const agencies    = useSelector(state => state.toJS().global.agencies || [])

  /* ── Non-admin state ── */
  const [orders,  setOrders]  = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState('')
  const [search,  setSearch]  = useState('')

  /* ── Admin state ── */
  const [agencySearch,    setAgencySearch]    = useState('')
  const [cmdFilter,       setCmdFilter]       = useState('')
  const [startDate,       setStartDate]       = useState('')
  const [endDate,         setEndDate]         = useState('')
  const [prodFilter,      setProdFilter]      = useState('')
  const [agencyOrders,    setAgencyOrders]    = useState({})
  const [expanded,        setExpanded]        = useState({})
  const [visibleAgencyIds, setVisibleAgencyIds] = useState(null)
  const [orderSearching,  setOrderSearching]  = useState(false)

  /* ── Non-admin fetch (on mount) ── */
  useEffect(() => {
    if (isAdmin || !agencyToken) return
    setLoading(true)
    fetch(`${apiBase}/order`, {
      headers: { 'Authorization': `Bearer ${agencyToken}` },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then(data => setOrders(parseOrders(data)))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [agencyToken, isAdmin])

  /* ── Lazy fetch for one agency (admin only) ── */
  const fetchOrdersForAgency = (agencyId) => {
    if (agencyOrders[agencyId]?.loaded) return
    setAgencyOrders(prev => ({
      ...prev,
      [agencyId]: { loading: true, error: '', orders: [], loaded: false },
    }))
    fetch(`${apiBase}/order?agencyId=${agencyId}`, {
      headers: { 'Authorization': `Bearer ${agencyToken}` },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then(data => {
        const list = parseOrders(data)
        setAgencyOrders(prev => ({
          ...prev,
          [agencyId]: { loading: false, error: '', orders: groupByOrderNumber(list), loaded: true },
        }))
      })
      .catch(e => {
        setAgencyOrders(prev => ({
          ...prev,
          [agencyId]: { loading: false, error: e.message, orders: [], loaded: true },
        }))
      })
  }

  const filterOrderList = (rawOrders) => {
    const filtered = rawOrders.filter(order => {
      if (cmdFilter.trim() && !(order.orderNumber || '').toLowerCase().includes(cmdFilter.trim().toLowerCase())) return false
      if (startDate) {
        const d = new Date(order.date)
        if (isNaN(d) || d < new Date(startDate)) return false
      }
      if (endDate) {
        const d    = new Date(order.date)
        const end  = new Date(endDate)
        end.setHours(23, 59, 59, 999)
        if (isNaN(d) || d > end) return false
      }
      if (prodFilter.trim()) {
        const q = prodFilter.trim().toLowerCase()
        if (!order.items.some(i => (i.name || '').toLowerCase().includes(q))) return false
      }
      return true
    })
    return filtered.slice(0, 50)
  }

  const handleSearch = async () => {
    const allUnique = deduplicateAgencies(agencies)
    const nameQ = agencySearch.trim().toLowerCase()
    const agencyList = nameQ
      ? allUnique.filter(ag => (ag.agencyName || ag.agencyname || '').toLowerCase().includes(nameQ))
      : allUnique

    const hasOrderFilter = cmdFilter.trim() || startDate || endDate || prodFilter.trim()
    if (!hasOrderFilter) {
      const matching = new Set(agencyList.map(ag => String(resolveAgencyId(ag))))
      setVisibleAgencyIds(matching)
      setExpanded(Object.fromEntries([...matching].map(id => [id, true])))
      agencyList.forEach(ag => fetchOrdersForAgency(resolveAgencyId(ag)))
      return
    }

    setOrderSearching(true)
    const matching = new Set()
    const newOrders = {}

    await Promise.allSettled(
      agencyList.map(async (ag) => {
        const agencyId = resolveAgencyId(ag)
        if (!agencyId) return
        try {
          let ordersData = agencyOrders[agencyId]
          if (!ordersData?.loaded) {
            const resp = await fetch(`${apiBase}/order?agencyId=${agencyId}`, {
              headers: { 'Authorization': `Bearer ${agencyToken}` },
            })
            const data = resp.ok ? await resp.json() : {}
            const list = parseOrders(data)
            ordersData = { loading: false, error: '', orders: groupByOrderNumber(list), loaded: true }
            newOrders[agencyId] = ordersData
          }
          if (filterOrderList(ordersData.orders || []).length > 0) matching.add(String(agencyId))
        } catch {
          // skip agency on fetch error
        }
      })
    )

    if (Object.keys(newOrders).length > 0) {
      setAgencyOrders(prev => ({ ...prev, ...newOrders }))
    }
    setVisibleAgencyIds(matching)
    setExpanded(Object.fromEntries([...matching].map(id => [id, true])))
    setOrderSearching(false)
  }

  const resetSearch = () => {
    setAgencySearch('')
    setCmdFilter('')
    setStartDate('')
    setEndDate('')
    setProdFilter('')
    setVisibleAgencyIds(null)
    setExpanded({})
  }

  const toggle = (agencyId) => {
    const wasOpen = !!expanded[agencyId]
    setExpanded(prev => ({ ...prev, [agencyId]: !wasOpen }))
    if (!wasOpen) fetchOrdersForAgency(agencyId)
  }

  /* ── Non-admin data ── */
  const flatRows = useMemo(() => groupByOrderNumber(orders), [orders])

  const filteredFlat = useMemo(() => {
    if (!search.trim()) return flatRows
    const q = search.trim().toLowerCase()
    return flatRows.filter(r =>
      (r.orderNumber || '').toLowerCase().includes(q) ||
      r.items.some(i => (i.name || '').toLowerCase().includes(q)) ||
      fmtDate(r.date).includes(q)
    )
  }, [search, flatRows])

  const filteredAgencies = useMemo(() => {
    const unique = deduplicateAgencies(agencies)
    if (visibleAgencyIds === null) return unique
    return unique.filter(ag => visibleAgencyIds.has(String(resolveAgencyId(ag))))
  }, [agencies, visibleAgencyIds])

  return (
    <PageWrapper>
      <PageHeader>
        <PageIconBox>
          <PageIconImg src={paymentIcon} alt='' />
        </PageIconBox>
        <div>
          <PageTitle>{isAdmin ? 'Achats' : 'Historique de mes achats'}</PageTitle>
          <PageSubtitle>
            {isAdmin
              ? `${agencies.length} agence${agencies.length > 1 ? 's' : ''}`
              : 'Toutes vos factures au même endroit'}
          </PageSubtitle>
        </div>
      </PageHeader>

      {isAdmin && (
        <FilterArea>
          <FilterRow>
            <FilterField>
              <FilterFieldLabel>Agence</FilterFieldLabel>
              <FilterSearchBox>
                <img src={searchIcon} alt='' />
                <input
                  value={agencySearch}
                  onChange={e => setAgencySearch(e.target.value)}
                  placeholder='Chercher une agence…'
                  style={{ width: '12rem' }}
                />
              </FilterSearchBox>
            </FilterField>
            <FilterField>
              <FilterFieldLabel>N° commande</FilterFieldLabel>
              <FilterSearchBox>
                <img src={searchIcon} alt='' />
                <input
                  value={cmdFilter}
                  onChange={e => setCmdFilter(e.target.value)}
                  placeholder='N° commande…'
                  style={{ width: '11rem' }}
                />
              </FilterSearchBox>
            </FilterField>
            <FilterField>
              <FilterFieldLabel>Date début</FilterFieldLabel>
              <FilterSearchBox>
                <input
                  type='date'
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={{ width: '10rem', paddingLeft: '0.75rem' }}
                />
              </FilterSearchBox>
            </FilterField>
            <FilterField>
              <FilterFieldLabel>Date fin</FilterFieldLabel>
              <FilterSearchBox>
                <input
                  type='date'
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  style={{ width: '10rem', paddingLeft: '0.75rem' }}
                />
              </FilterSearchBox>
            </FilterField>
            <FilterField>
              <FilterFieldLabel>Produit</FilterFieldLabel>
              <FilterSearchBox>
                <img src={searchIcon} alt='' />
                <input
                  value={prodFilter}
                  onChange={e => setProdFilter(e.target.value)}
                  placeholder='Nom du produit…'
                  style={{ width: '12rem' }}
                />
              </FilterSearchBox>
            </FilterField>
            {(agencySearch.trim() || cmdFilter.trim() || startDate || endDate || prodFilter.trim() || visibleAgencyIds !== null) && (
              <>
                <FilterActionBtn onClick={handleSearch} disabled={orderSearching}>
                  <img src={searchIcon} alt='' />
                  {orderSearching ? 'Recherche…' : 'Rechercher'}
                </FilterActionBtn>
                <FilterResetBtn onClick={resetSearch}>✕ Réinitialiser</FilterResetBtn>
              </>
            )}
          </FilterRow>
        </FilterArea>
      )}

      {!isAdmin && (
        <SearchArea>
          <SearchWrapper>
            <SearchImg src={searchIcon} alt='' />
            <SearchInput
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder='Recherchez un numéro de commande, un produit, une date…'
            />
            <SearchButton type='button'>Rechercher</SearchButton>
          </SearchWrapper>
        </SearchArea>
      )}

      <TableArea>
        <TableCard>

          {isAdmin && (
            <>
              {filteredAgencies.length === 0 && (
                <EmptyRow>Aucune agence ne correspond à votre recherche.</EmptyRow>
              )}

              {filteredAgencies.map(ag => {
                const agencyId  = resolveAgencyId(ag)
                const name      = ag.agencyName || ag.agencyname || agencyId
                const open      = !!expanded[agencyId]
                const agData    = agencyOrders[agencyId]
                const isLoading = agData?.loading
                const hasLoaded = agData?.loaded
                const agErr     = agData?.error
                const rawOrders = agData?.orders || []
                const orderList = filterOrderList(rawOrders)

                return (
                  <AgencyBlock key={agencyId} id={agencyId}>
                    <AgencyRow $open={open} onClick={() => toggle(agencyId)}>
                      <ArrowBtn $open={open}>▶</ArrowBtn>
                      <AgencyNameText>{name}</AgencyNameText>
                      <AgencyStats>
                        {isLoading && (
                          <StatPill style={{ opacity: 0.5 }}>Chargement…</StatPill>
                        )}
                        {hasLoaded && agErr && (
                          <StatPill style={{ background: '#FFE4E4', color: '#E53E3E', borderColor: '#FCA5A5' }}>
                            Erreur
                          </StatPill>
                        )}
                      </AgencyStats>
                    </AgencyRow>

                    <SubTableWrap $open={open}>
                      {isLoading && <EmptyRow style={{ padding: '1.25rem' }}>Chargement des commandes…</EmptyRow>}
                      {hasLoaded && agErr && <EmptyRow style={{ padding: '1.25rem', color: '#E53E3E' }}>Erreur : {agErr}</EmptyRow>}
                      {hasLoaded && !agErr && orderList.length === 0 && (
                        <EmptyRow style={{ padding: '1.25rem' }}>Aucune commande pour cette agence.</EmptyRow>
                      )}
                      {hasLoaded && !agErr && orderList.length > 0 && (
                        <>
                          <SubTableHead>
                            <SubHeadCell $w='22%'>N° commande</SubHeadCell>
                            <SubHeadCell $w='9%'>Date</SubHeadCell>
                            <SubHeadCell $flex='1'>Produit</SubHeadCell>
                            <SubHeadCell $w='11%' $align='center'>Prix</SubHeadCell>
                            <SubHeadCell $w='12%' $align='center'>Statut</SubHeadCell>
                            <SubHeadCell $w='12%' $align='center'>Facture</SubHeadCell>
                          </SubTableHead>

                          {orderList.map(order => (
                            <SubRow key={order.key}>
                              <SubCell $w='22%' $muted style={{ fontSize: '0.72rem' }}>
                                {order.orderNumber}
                              </SubCell>
                              <SubCell $w='9%'>{fmtDate(order.date)}</SubCell>
                              <SubCell $flex='1' $wrap>
                                {order.items.map((item, i) => (
                                  <div key={i}>{item.name} × {item.quantity || 1}</div>
                                ))}
                              </SubCell>
                              <SubCell $w='11%' $bold $align='center'>
                                {fmtPrice(order.totalAmount)}
                              </SubCell>
                              <SubCell $w='12%' $align='center'>
                                <StatusBadge status={order.status}>
                                  {STATUS_LABELS[order.status] || order.status}
                                </StatusBadge>
                              </SubCell>
                              <SubCell $w='12%' $align='center'>
                                <ActionBtn onClick={() => {}}>
                                  <img src={downloadIcon} alt='' />
                                  Facture
                                </ActionBtn>
                              </SubCell>
                            </SubRow>
                          ))}
                        </>
                      )}
                    </SubTableWrap>
                  </AgencyBlock>
                )
              })}
            </>
          )}

          {!isAdmin && (
            <>
              {loading  && <EmptyRow>Chargement des commandes…</EmptyRow>}
              {!loading && error && <EmptyRow>Erreur : {error}</EmptyRow>}
              {!loading && !error && filteredFlat.length === 0 && (
                <EmptyRow>
                  {orders.length === 0
                    ? 'Aucune commande pour le moment.'
                    : 'Aucune commande ne correspond à votre recherche.'}
                </EmptyRow>
              )}
              {!loading && !error && filteredFlat.length > 0 && (
                <>
                  <TableHeader>
                    <HeaderCell $w='12%'>Date</HeaderCell>
                    <HeaderCell $w='26%'>N° commande</HeaderCell>
                    <HeaderCell $flex='1'>Produit</HeaderCell>
                    <HeaderCell $w='12%' $align='center'>Prix TTC</HeaderCell>
                    <HeaderCell $w='13%' $align='center'>Statut</HeaderCell>
                    <HeaderCell $w='13%' $align='center'>Actions</HeaderCell>
                  </TableHeader>
                  <TableBody>
                    {filteredFlat.map(row => (
                      <TableRow key={row.key}>
                        <Cell $w='12%'>{fmtDate(row.date)}</Cell>
                        <Cell $w='26%' style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                          {row.orderNumber}
                        </Cell>
                        <Cell $flex='1' $wrap>
                          {row.items.map((item, i) => (
                            <div key={i}>{item.name} × {item.quantity || 1}</div>
                          ))}
                        </Cell>
                        <Cell $w='12%' $bold $align='center'>
                          {fmtPrice(row.totalAmount)}
                        </Cell>
                        <Cell $w='13%' $align='center'>
                          <StatusBadge status={row.status}>
                            {STATUS_LABELS[row.status] || row.status}
                          </StatusBadge>
                        </Cell>
                        <Cell $w='13%' $align='center'>
                          <ActionBtn onClick={() => {}}>
                            <img src={downloadIcon} alt='' />
                            Facture
                          </ActionBtn>
                        </Cell>
                      </TableRow>
                    ))}
                  </TableBody>
                </>
              )}
            </>
          )}

        </TableCard>
      </TableArea>
    </PageWrapper>
  )
}

export default MyPurchases
